import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Loader2, ArrowLeft, ArrowRight, ShieldCheck } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { formatApiErrorDetail } from "@/lib/api";
import { LOGO_URL, HERO_IMG, DOMAIN } from "@/lib/constants";
import { useLang } from "@/i18n/LanguageContext";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";

const easing = [0.22, 1, 0.36, 1];

export default function Login() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const { t } = useLang();
  const navigate = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const user = await login(email, password);
      toast.success(t("login.success"));
      navigate(user.role === "admin" ? "/admin" : "/creator");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || t("login.fail"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="grain min-h-screen bg-[#050505] lg:grid lg:grid-cols-2">
      <aside className="relative hidden lg:flex flex-col justify-between overflow-hidden border-e border-white/10 p-14 xl:p-16">
        <motion.img src={HERO_IMG} alt="" initial={{ scale: 1.15, opacity: 0 }} animate={{ scale: 1, opacity: 0.35 }} transition={{ duration: 1.6, ease: easing }} className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-br from-[#050505]/70 via-[#050505]/80 to-[#050505]" />
        <div className="absolute inset-0 spotlight" />

        <div className="relative z-10">
          <Link to="/" data-testid="login-brand-home" className="inline-flex items-center gap-2 text-sm text-zinc-400 hover:text-white transition-colors">
            <ArrowLeft size={16} /> {t("login.back")}
          </Link>
        </div>

        <div className="relative z-10 flex flex-col items-center text-center flex-1 justify-center">
          <div className="relative">
            <motion.img src={LOGO_URL} alt="AURA+" data-testid="login-brand-logo" initial={{ opacity: 0, y: 24, scale: 0.92 }} animate={{ opacity: 1, y: 0, scale: 1 }} transition={{ duration: 1.1, ease: easing, delay: 0.15 }} className="relative w-[300px] xl:w-[360px] h-auto mix-blend-screen" />
          </div>
          <motion.p initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, ease: easing, delay: 0.5 }} className="mt-10 max-w-sm text-zinc-400 leading-relaxed">
            {t("login.brandDesc")} {t("login.brandDesc2")}
          </motion.p>
        </div>

        <div className="relative z-10 flex items-center gap-2 text-xs uppercase tracking-[0.25em] text-zinc-500">
          <span className="h-1.5 w-1.5 rounded-full bg-emerald-400" /> AURA+ Creator Program
        </div>
      </aside>

      <main className="flex flex-col min-h-screen lg:min-h-0">
        <div className="p-5 flex items-center justify-between lg:justify-end">
          <Link to="/" data-testid="login-back-link" className="inline-flex items-center gap-2 text-sm text-zinc-400 hover:text-white transition-colors lg:hidden">
            <ArrowLeft size={16} /> {t("login.back")}
          </Link>
          <LanguageSwitcher />
        </div>

        <div className="flex-1 flex items-center justify-center px-5 py-6 lg:px-16">
          <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, ease: easing }} className="w-full max-w-md">
            <div className="lg:hidden flex flex-col items-center text-center mb-10">
              <img src={LOGO_URL} alt="AURA+" data-testid="login-mobile-logo" className="w-48 h-auto mix-blend-screen mb-2" />
            </div>

            <div className="mb-9">
              <span className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-500">{t("login.badge")}</span>
              <h1 className="font-display text-4xl md:text-5xl font-bold text-white tracking-tight mt-4 leading-[1.02]">{t("login.heading")}</h1>
              <p className="mt-4 text-zinc-500">{t("login.subtitle")}</p>
            </div>

            <form onSubmit={submit} data-testid="login-form" className="space-y-5">
              <div>
                <label className="block text-xs uppercase tracking-[0.12em] text-zinc-500 mb-2">{t("login.email")}</label>
                <input data-testid="login-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} className="w-full bg-transparent border border-aura-border rounded-xl px-4 py-3.5 text-white placeholder:text-zinc-600 focus:border-white/50 focus:ring-1 focus:ring-white/20 outline-none transition-colors" placeholder="tu@email.com" autoComplete="email" />
              </div>
              <div>
                <label className="block text-xs uppercase tracking-[0.12em] text-zinc-500 mb-2">{t("login.password")}</label>
                <input data-testid="login-password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} className="w-full bg-transparent border border-aura-border rounded-xl px-4 py-3.5 text-white placeholder:text-zinc-600 focus:border-white/50 focus:ring-1 focus:ring-white/20 outline-none transition-colors" placeholder="••••••••" autoComplete="current-password" />
              </div>
              <button type="submit" disabled={loading} data-testid="login-submit-btn" className="group w-full rounded-full bg-white text-black font-semibold py-4 hover:bg-zinc-200 active:scale-[0.99] transition-[background-color,transform] disabled:opacity-60 flex items-center justify-center gap-2">
                {loading ? <><Loader2 size={18} className="animate-spin" /> {t("login.signing")}</> : (<>{t("login.submit")} <ArrowRight size={17} className="group-hover:translate-x-0.5 transition-transform" /></>)}
              </button>
            </form>

            <div className="mt-8 flex items-center gap-2 text-xs text-zinc-600"><ShieldCheck size={14} /> {t("login.secure")} · {DOMAIN}</div>

            <div className="mt-8 pt-6 border-t border-white/10">
              <p className="text-sm text-zinc-500">
                {t("login.noAccount")}{" "}
                <Link to="/#apply" data-testid="login-apply-link" className="text-white hover:underline underline-offset-4">{t("login.applyLink")}</Link>
              </p>
            </div>
          </motion.div>
        </div>
      </main>
    </div>
  );
}
